import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  IconButton,
  Pagination,
  Skeleton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Card,
  CardContent,
  CardActions,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  LocationOn as LocationOnIcon,
  ShoppingCart as ShoppingCartIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { RESULTS_PER_PAGE, useAllOrders } from "../../services/useAllOrders";
import type { Order } from "../OrderDetailsModal";

interface AllOrdersModalProps {
  open: boolean;
  onClose: () => void;
  onOrderDetails: (order: Order) => void;
  seedSlug: string;
}

const ORDER_STATUS_MAP: Record<string, string> = {
  ORDERED: "Pedido",
  PAID: "Pago",
  CANCELED: "Cancelado",
  FINISHED: "Finalizado",
};

const ORDER_STATUS_COLORS = {
  ORDERED: "info",
  PAID: "warning",
  CANCELED: "error",
  FINISHED: "success",
} as const;

// Componente de card para pedidos no mobile
const OrderCard = ({
  order,
  onViewDetails,
}: {
  order: Order;
  onViewDetails: (order: Order) => void;
}) => {
  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 2,
        border: "1px solid #e0e0e0",
        boxShadow: "none",
        "&:hover": {
          borderColor: "#bdbdbd",
        },
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" component="div" color="primary.main">
            Pedido #{order.id}
          </Typography>
          <Chip
            label={ORDER_STATUS_MAP[order.status] || order.status}
            color={
              ORDER_STATUS_COLORS[
                order.status as keyof typeof ORDER_STATUS_COLORS
              ] || "default"
            }
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <LocationOnIcon
              sx={{ color: "text.secondary", mr: 1, fontSize: 18 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {order.address}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ShoppingCartIcon
              sx={{ color: "text.secondary", mr: 1, fontSize: 18 }}
            />
            <Typography variant="body2" color="text.secondary">
              {order.items_count} {order.items_count === 1 ? "item" : "itens"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CalendarTodayIcon
              sx={{ color: "text.secondary", mr: 1, fontSize: 18 }}
            />
            <Typography variant="body2" color="text.secondary">
              {dayjs(order.created_at).format("DD/MM/YYYY")}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AttachMoneyIcon
              sx={{ color: "text.secondary", mr: 1, fontSize: 18 }}
            />
            <Typography variant="body1" fontWeight={600} color="text.primary">
              R$ {order.total.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onViewDetails(order)}
          fullWidth
          sx={{ borderRadius: 2 }}
        >
          Ver detalhes
        </Button>
      </CardActions>
    </Card>
  );
};

const AllOrdersModal: React.FC<AllOrdersModalProps> = ({
  open,
  onClose,
  onOrderDetails,
  seedSlug,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useAllOrders({ seedSlug, page });

  const totalPages = data ? Math.ceil(data.count / RESULTS_PER_PAGE) : 1;

  const handlePageChange = (_: any, value: number) => {
    setPage(value);
  };

  // Renderizar conteúdo principal
  const renderMainContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {Array.from(new Array(5)).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              height={isMobile ? 120 : 60}
            />
          ))}
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error">
          Erro ao carregar pedidos. Verifique sua conexão e tente novamente.
        </Alert>
      );
    }

    if (data.results.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <ReceiptIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Você ainda não fez pedidos
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Quando você fizer seus primeiros pedidos, eles aparecerão aqui.
          </Typography>
        </Box>
      );
    }

    // Exibição responsiva: Cards no mobile, Tabela no desktop
    if (isMobile) {
      return (
        <Box
          sx={{
            mt: 2,
            flex: 1,
            overflow: "auto",
            pr: 1, // Padding para a scrollbar
          }}
        >
          {data.results.map((order: Order) => (
            <OrderCard
              key={order.id}
              order={order}
              onViewDetails={onOrderDetails}
            />
          ))}
        </Box>
      );
    }

    return (
      <Box sx={{ overflowX: "auto" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Endereço</TableCell>
                <TableCell>Itens</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.results.map((order: Order) => (
                <TableRow key={order.id}>
                  <TableCell>{order.id}</TableCell>
                  <TableCell>{order.address}</TableCell>
                  <TableCell>{order.items_count}</TableCell>
                  <TableCell>
                    {dayjs(order.created_at).format("DD/MM/YYYY")}
                  </TableCell>
                  <TableCell>R$ {order.total.toFixed(2)}</TableCell>
                  <TableCell>
                    {ORDER_STATUS_MAP[order.status] || order.status}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => onOrderDetails(order)}
                      variant="outlined"
                    >
                      Ver detalhes
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          width: "95vw",
          height: "90vh",
          maxWidth: "1200px",
          margin: "auto",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h5"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <ReceiptIcon sx={{ mr: 1, color: "primary.main" }} />
            Histórico de Pedidos
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          overflow: "auto",
          p: 3,
        }}
      >
        <Box sx={{ mt: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {isLoading
                ? "Carregando..."
                : `${data.count} pedido${data.count !== 1 ? "s" : ""} encontrado${data.count !== 1 ? "s" : ""}`}
            </Typography>

            {totalPages > 1 && (
              <Typography variant="body2" color="text.secondary">
                Página {page} de {totalPages}
              </Typography>
            )}
          </Box>

          <Divider sx={{ mt: 2 }} />
        </Box>

        {/* Conteúdo Principal */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {renderMainContent()}
        </Box>

        {/* Paginação */}
        {!isLoading && !error && totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              size="large"
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AllOrdersModal;
